import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle, ButtonGroup, Tooltip } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useGetItemQuery, useDeletePropertyMutation, useUpdatePropertyMutation } from '../store/docApi';
import EditableProperty from '../components/widgets/EditableProperty';
import ItemMetadata from '../components/widgets/ItemMetadata';
import Picture from '../components/widgets/Picture';
import ColorChart from '../components/charts/ColorChart';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';
import KnitChart from '../components/charts/KnitChart';
import StepViewer from '../Instructions/StepViewer';
import StepEditor from '../Instructions/StepEditor';
import Links from '../components/widgets/Links';

export default function PartDetails() {
  const { pk, id, subId } = useParams();
  const [part, setPart] = useState(null);
  const { data: item } = useGetItemQuery({ label: 'workspace', pk, id });
  const [deleteProperty] = useDeletePropertyMutation();
  const [updateProperty] = useUpdatePropertyMutation();
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isEditingSteps, setIsEditingSteps] = useState(false);

  useEffect(() => {
    if (item && item.parts && subId && item.parts[subId]) {
      setPart(item.parts[subId]);
    }
  }, [item, subId]);

  function deletePart() {
    setDeleteConfirmOpen(false);
    deleteProperty({ pk: item.pk, id: item.id, name: `parts/${subId}` });
    navigate(`/workspaces/${item.pk}/${item.id}`);
  }

  const handleSaveSteps = async (updatedSteps) => {
    try {
      await updateProperty({
        label: item.label,
        pk: item.pk,
        id: item.id,
        name: `parts/${subId}/steps`,
        value: updatedSteps
      });
      setIsEditingSteps(false);
    } catch (error) {
      console.error('Error saving steps:', error);
    }
  };

  return (
    part && (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {part.name || 'Unnamed Part'}
            </Typography>
            {part.description && (
              <Typography variant="body2" color="text.secondary">
                {part.description}
              </Typography>
            )}
          </Box>
          <ButtonGroup variant="outlined" size="small">
            <Tooltip title="Delete Part">
              <Button startIcon={<DeleteIcon />} color="error" onClick={() => setDeleteConfirmOpen(true)}>
                Delete
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>

        <Box>
          <EditableProperty
            item={item}
            title="Name"
            propname={`parts/${subId}/name`}
            propvalue={part.name}
            placeholder="Enter part name..."
          />
        </Box>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
          <Typography variant="h5" gutterBottom>Instructions</Typography>
          <Tooltip title="Edit Steps">
            <Button
              startIcon={<EditIcon />}
              onClick={() => setIsEditingSteps(!isEditingSteps)}
            >
              {isEditingSteps ? 'View' : 'Edit'} Steps
            </Button>
          </Tooltip>
        </div>
        {part.author && <div>Designed by {part.author} </div>}
        {part.cols > 1 && <div>Multiple of {part.cols} sts {part.extracols && <span>plus {part.extracols}</span>}</div>}
        <Picture picture={part.picture} pictures={part.pictures} />
        {part.description}
        {false && part.colorchart && <ColorChart part={part} chartid={part.id} />}
        {part.colorchart && <ColorChartThumbnail data={part.data} colors={part.colors} mincols={24} />}
        {part.knitchart && part.steps && <div><br /><KnitChart steps={part.steps} part={part} chartid={part.id} /></div>}
        {part.steps && (
          isEditingSteps ? (
            <StepEditor
              steps={part.steps}
              onSave={handleSaveSteps}
              parts={item.parts || []}
            />
          ) : (
            <StepViewer steps={part.steps} showVerbal={true} expandAll={true} />
          )
        )}
        <Links links={part.links} />
        {part.sources && part.sources.map((source, i) => {
          return <div key={i}>{source.id} {source.chapter} {source.page}</div>
        })}

        <ItemMetadata item={item} subId={subId} />

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this part "{part.name}"?</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deletePart}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    )
  );
}
